import Modal from "./Modal"
import { useState } from "react"
import { cn } from "../lib/utils"

/**
 * A component that renders a clickable link to open a modal displaying a video or PDF.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.src="https://player.vimeo.com/video/1034550892?title=0&byline=0"] - The source URL of the video or PDF to be displayed in the modal.
 * @param {string} [props.textClassName] - Optional additional class names for styling the link text.
 * @param {string} [props.text="Click here "] - Text displayed as the clickable link.
 * @param {string} [props.postText=" to watch a video"] - Text appended after the clickable link.
 * @param {string} [props.type="vimeo"] - The type of content to be displayed, either "vimeo" (default) or "pdf".
 * @param {string} [props.icon="fa-solid fa-clapperboard-play"] - Optional icon class name for an icon displayed alongside the clickable link text.
 * @param {string} [props.panelClassName] - Additional class names for the modal panel's styling.
 * @param {string} [props.title] - An optional title displayed above the content in the modal.
 *
 * @returns {JSX.Element} A clickable link that opens a modal displaying a video or PDF.
 */
export const MoreInfoVideoModal = ({
  src = "https://player.vimeo.com/video/1034550892?title=0&byline=0",
  textClassName,
  text = "Click here ",
  postText = " to watch a video",
  type = "vimeo",
  icon = "fa-solid fa-clapperboard-play",
  panelClassName,
  title
}) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <a
        onClick={() => setIsOpen(true)}
        className={cn("cursor-pointer text-brandSecondary underline", textClassName)}
      >
        {text}
        {icon && <i className={cn(text !== "" ? "ml-1" : "", "mr-1", icon)}></i>} {postText}
      </a>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        panelClassName={cn(type === "pdf" && "md:h-[90vh]", panelClassName)}
      >
        <div
          className={cn(
            "relative print:hidden",
            type === "pdf" ? "h-[calc(90vh-8rem)]" : "pt-[56.25%]",
          )}
        >
          {title && (<div className={cn("text-xl font-bold mb-2 absolute left-0 -top-4")}>{title}</div>)}
          <iframe
            src={src}
            className="absolute left-0 top-4 h-full w-full"
            allow="autoplay; fullscreen; picture-in-picture"
          ></iframe>
        </div>
        {type === "vimeo" && <script src="https://player.vimeo.com/api/player.js"></script>}
      </Modal>
    </>
  )
}
